import { useEffect, useRef, useState } from 'react';

import { MultiSelect, MultiSelectType } from 'components/shared/form/InfiniteScrollDropdown';
import { useDebounce } from 'hooks/useDebounce';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import { FilterOption } from 'stores/types';

export type FilterMultiSelectProps = Partial<MultiSelectType<FilterOption>> & {
  name: string;
  options?: FilterOption[];
  debounceTime?: number;
  getOptions?: {
    hook: any;
    params?: object;
    optionsToPrepend?: FilterOption[];
  };
};

const FilterMultiSelect = (props: FilterMultiSelectProps) => {
  const {
    name,
    options,
    getOptions,
    getOptionLabel,
    getOptionValue,
    placeholder,
    value,
    onChange,
    debounceTime = 0,
  } = props;

  const asyncOptions = options ? { options } : getOptions?.hook(getOptions?.params);

  const [selectedOptions, setSelectedOptions] = useState<FilterOption[]>(value ?? []);
  const prevValueRef = useRef(value);
  const isUserChangeRef = useRef(false);

  useEffect(() => {
    if (value !== prevValueRef.current) {
      isUserChangeRef.current = false;
      setSelectedOptions(value ?? []);
      prevValueRef.current = value;
    }
  }, [value]);

  const debouncedSelectedOptions = useDebounce(selectedOptions, debounceTime);

  useEffect(() => {
    if (isUserChangeRef.current) {
      onChange?.(new FilterValueArray(debouncedSelectedOptions));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSelectedOptions]);

  const handleDropdownChange = (changes: FilterValueArray) => {
    isUserChangeRef.current = true;
    setSelectedOptions(changes);
  };

  return (
    <div data-cy={`${name}Filter`}>
      <MultiSelect
        {...asyncOptions}
        getOptionValue={getOptionValue}
        getOptionLabel={getOptionLabel}
        placeholder={placeholder}
        onChange={handleDropdownChange}
        value={selectedOptions}
        prependedOptions={getOptions?.optionsToPrepend || []}
      />
    </div>
  );
};

export default FilterMultiSelect;
